/**
 * Order Render
 *
 * This module provides functions around Order Detail.
 * - Virtual detail page generation (render/window history)
 * - Dependancy on our custom shopify theme
 */
import { MUAPP } from 'account/api';
import { state } from 'account/state';
import {
  orderObjectReducer,
  orderDetailObjectReducer,
  displayOrderId,
  permissionErrorHandler,
} from 'account/utils';

// Static variables
const listClassName = 'js-orders-list';
const listContainerClassName = `${listClassName}-container`;
const listActiveClassName = `${listClassName}--active`;

const detailClassName = 'js-order-detail';
const detailIdClassName = `${detailClassName}-id`;
const detailStatusClassName = `${detailClassName}-status`;
const detailContainerClassName = `${detailClassName}-container`;
const detailActiveClassName = `${detailClassName}--active`;

const invoiceLink = `js-invoice-link`;
const statusToggleClassName = `js-status-type`;
const dateToggleName = `dateType`;
const paginationClassName = `js-pagination`;
const paginationContainerClassName = `${paginationClassName}-container`;
const showInventoryClassName = `orders-list__inventory`;
const allDetailsButton = `btn-all-details-toggle`;
let searchChangedTimeout = null;

let searchState = {
  currentPage: 1,
  statusType: 'all',
  searchTimestamp: null,
};
const dateText = (date) => {
  switch (date) {
    case '1y':
      return 'past year';
    case '6mo':
      return 'past 6 months';
    case '3mo':
      return 'past 3 months';
    case '1mo':
      return 'past month';
    case '1w':
      return 'past week';
    default:
      return 'All';
  }
};

/**
 * renderOrdersList
 *
 * @param {object} orders - spars order data
 */
const renderOrdersList = (orders) => {
  if (!$(`.${listClassName}`)) return;
  const $ordersList = $(`.${listClassName}`);
  let ordersObj = {};
  let output = '';
  let promises = [];
  orders.forEach((order, index, arr) => {
    // Total items
    arr[index].TotalItems = order.Header.TotalItems;
    // Product Images, shows only first 4
    arr[index].Detail = order.Detail.slice(0, 4);
    arr[index].Detail.forEach((detail) => {
      if (!detail.ShopifyItem.image) {
        detail.ShopifyItem.image = orderNoImgSrc;
        detail.imageless = true;
      } else {
        detail.imageless = false;
      }
    });
    // To add additional attirbutes in order object
    arr[index] = orderObjectReducer(order);

    output += window.orderCardTemplate
      ? window.orderCardTemplate.render(arr[index])
      : JSON.parse(arr[index]);
  });

  // If no order, show message and hide search field
  let $button = null;
  if (orders.length <= 0) {
    const query = $('#searchText').val();
    const queryType = $('#searchType').val();
    const queryDate = $('#dateType').val();
    const typeText = (text) => {
      switch (text) {
        case 'ItemName':
          return 'Product Name';
        case 'CustomerPO':
          return 'PO Number';
        case 'OrderNo':
          return 'Order Number';
        default:
          return 'Product Name';
      }
    };
    const queryTypeText = (text, queryStatus, queryDate) => {
      if (text === 'ItemName') {
        let returnString =
          'the collection name or design ID is spelled correctly';
        if (queryStatus !== 'all' && queryDate !== 'All') {
          returnString += ', try broadening your date range/status,';
        } else if (queryDate !== 'All') {
          returnString += ', try broadening your date range,';
        } else if (queryStatus !== 'all') {
          returnString += ', try broadening your status,';
        }
        return returnString;
      } else if (text === 'CustomerPO') {
        return 'your PO number is correct';
      } else if (text === 'OrderNo') {
        return 'your order number is correct';
      } else {
        return 'the collection name or design ID is spelled correctly, try broadening your date range/status,';
      }
    };
    const queryTypeString = queryType === 'ItemName' ? '' : typeText(queryType);
    const search = $('#searchText').val()
      ? `Your search for ${queryTypeString} "${query}" `
      : 'Your search';
    const date = queryDate === 'All' ? '' : `within the ${dateText(queryDate)}`;
    if (!query && queryDate === 'All' && searchState.statusType === 'all') {
      output = `<h3 class='no-orders'>You have no order history yet.</h3>`;
    } else {
      $button = $(
        '<button class="order-reset-statement--trigger underline" type="button">Reset Search</button>',
      );
      $button.on('click', clearAllSearchFilters);
      output = `<h3 class='no-orders'>${search} ${date} produced no results.</h3><p class="no-orders no-orders--small mt-5">Ensure ${queryTypeText(
        queryType,
        searchState.statusType,
        queryDate,
      )} or <span id="searchErrorResetContainer"></span> to see all your order history.</p>`;
    }
    $('#orderSearchField').addClass('hidden');
    $('#orderResetSidebar').removeClass('md-up:block');
    $('#orderResetMobile').addClass('hidden');
  } else {
    $('#orderResetSidebar').addClass('md-up:block');
    $('#orderResetMobile').removeClass('hidden');
  }

  $(`.${listContainerClassName}`).html('').append(output);
  if ($button) {
    $('#searchErrorResetContainer').append($button);
  }

  // set up invoice link
  $(`.${invoiceLink}`).on('click', downloadInvoice);

  // Initialized Search
  //TODO: remove all traces of old search
  window.pubsub.trigger('orderHistoryRendered');
};

const downloadInvoice = (evt) => {
  const orderID = $(evt.target).data('order');
  const invoiceID = $(evt.target).data('link');
  $(evt.target)[0].innerHTML = 'loading...';
  MUAPP.orders.downloadInvoiceById(orderID, invoiceID, function () {
    $(evt.target)[0].innerHTML = 'View Invoice';
  });
};

/**
 * renderOrderDetail
 * @function
 *
 * @param {string} id - SPARS(Shopify) order ID
 *
 * if url query string has 'order-history' view attribute & id
 */
const renderOrderDetail = (order = null, id = null) => {
  if (!$(`.${detailClassName}`)) return;

  // Array of Header fields to transform to lowercase so we can capitalize each word
  const headerFields = [
    'ShippingFirstName',
    'ShippingLastName',
    'ShippingAddress1',
    'ShippingAddress2',
    'ShippingCity',
    'BillingFirstName',
    'BillingLastName',
    'BillingAddress1',
    'BillingAddress2',
    'BillingCity',
  ];

  // Transform header fields to lowercase
  headerFields.forEach((field) => {
    if (order.Header[field]) {
      order.Header[field] = order.Header[field].toLowerCase();
    }
  });

  // To add additional attributes in order object
  order = orderDetailObjectReducer(order);

  let output = '';
  let header = window.orderDetailHeader
    ? window.orderDetailHeader.render(order)
    : JSON.parse(order);

  const addShipmentDetails = (shipments) => {
    output +=
      '<div class="order-shipment-info rounded flex flex-col fade-in md-up:border md-up:border-neutral-30 pt-6 md-up:pt-10">';
    output += window.orderShipmentInfo
      ? window.orderShipmentInfo.render(shipments[0])
      : '';
    output += `<div class="flex ${
      shipments[0].isShipment ? 'flex-col' : 'justify-between flex-wrap gap-6'
    }">`;

    // Render items that are Pending only once
    if (shipments[0].isPending) {
      output += window.orderShipmentProducts
        ? window.orderShipmentProducts.render(shipments[0])
        : '';
    } else if (!shipments[0].isShipment) {
      // Handle other cases (preparing to ship, canceled, etc)
      for (var i = 0; i < shipments.length; i++) {
        output += window.orderShipmentProducts
          ? window.orderShipmentProducts.render(shipments[i])
          : '';
      }
    } else {
      // Handles all shipments
      for (var i = 0; i < shipments.length; i++) {
        output += `
          <div class="${i === 0 ? 'pb-6 md-up:pb-10' : 'py-6 md-up:py-10'} ${
          i < shipments.length - 1 ? 'border-b border-b-neutral-30' : ''
        }">
            <div class='flex flex-col gap-3 md-up:gap-2 mb-4 md-up:mb-6'>
              <div class='flex justify-between items-center'>
                <p class='title-small'>Shipment ${i + 1}</p>
                ${
                  shipments[i].Tracking
                    ? `<a
                    class='utility-xsmall flex gap-2 items-center justify-center py-1 px-4 max-h-max'
                    href='${shipments[i].Tracking}'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <span class="open-in-new"></span> Track Shipment
                  </a>`
                    : ''
                }
              </div>
              ${
                shipments[i].Tracking
                  ? `<span class="block utility-xsmall">Tracking information may take up to 24 hours to display</span>`
                  : ''
              }
            </div>
        `;
        output += window.orderShipmentProducts
          ? window.orderShipmentProducts.render(shipments[i])
          : '';
        output += '</div>';
      }
    }
    output += '</div></div>';
  };
  // Render
  output = '<div class="md-up:grid md-up:grid-cols-12 md-up:gap-6">';
  output += window.orderDetailsContainer
    ? window.orderDetailsContainer.render(order)
    : JSON.parse(order);
  output += '<div class="flex flex-col md-up:gap-10 col-span-8 col-end-13">';

  if (order.Shipments.length > 0) {
    const totalShippedItems = order.Shipments.reduce((total, shipment) => {
      return (
        total +
        shipment.Detail.reduce((shipmentTotal, item) => {
          return shipmentTotal + (parseInt(item.Qty) || 0);
        }, 0)
      );
    }, 0);

    let shipments = [];
    for (var i = 0; i < order.Shipments.length; i++) {
      let shipment = order.Shipments[i];
      shipment.isDelivery = shipment.DeliveryDate ? true : false;
      shipment.isShipment = shipment.DeliveryDate ? false : true;
      let unformattedDate = new Date(shipment.shippingDate);
      shipment.dateFormatted = unformattedDate
        ? custom.utils.monthDayYear(unformattedDate)
        : '';
      // TODO: confirm this date will never be used and then remove completely
      // if(shipment.isDelivery) {
      //   const unformattedDate = new Date(shipment.shippingDate);
      //   shipment.dateFormatted = unformattedDate ? custom.utils.monthDayYear(unformattedDate) : '';
      // }
      shipment.totalQuantity = totalShippedItems;
      shipments.push(shipment);
    }

    addShipmentDetails(shipments);
  }
  if (order.PreparingToShip.length > 0) {
    // Calculate total pending items
    const totalPreparingToShipItems = order.PreparingToShip.reduce(
      (total, item) => {
        return total + (parseInt(item.Qty) || 0);
      },
      0,
    );

    let shipments = [];
    for (var i = 0; i < order.PreparingToShip.length; i++) {
      let shipment = {
        isPreparing: true,
        status: order.PreparingToShip[i].Status,
        Detail: [order.PreparingToShip[i]],
        totalQuantity: totalPreparingToShipItems,
      };
      shipments.push(shipment);
    }

    addShipmentDetails(shipments);
  }
  if (order.Pendings.length > 0) {
    let shipments = [];

    function addBusinessDays(date, days) {
      let currentDate = new Date(date);
      let addedDays = 0;

      while (addedDays < days) {
        currentDate.setDate(currentDate.getDate() + 1);
        // 0 is Sunday, 6 is Saturday
        if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
          addedDays++;
        }
      }

      return currentDate;
    }

    function formatDate(date, type) {
      if (!date || date === '0' || date === '') return 'TBD';

      const originalDate = new Date(date);
      if (isNaN(originalDate.getTime())) return '';

      let formattedDate;

      // Add days based on type; 4 days for the start date and 9 days for the end date
      if (type === 'start') {
        formattedDate = addBusinessDays(originalDate, 4); // 4 business days
      } else if (type === 'end') {
        formattedDate = addBusinessDays(originalDate, 9); // 9 business days
      }

      const month = formattedDate.toLocaleString('en-US', { month: 'short' });
      const day = formattedDate.getDate();

      return `${month} ${day}`;
    }

    // Calculate total pending items
    const totalPendingItems = order.Pendings.reduce((total, item) => {
      return total + (parseInt(item.Qty) || 0);
    }, 0);

    const inStockItems = order.Pendings.filter(
      (item) => item.Status === 'In Stock',
    );
    const backOrderItems = order.Pendings.filter(
      (item) => item.Status === 'On BackOrder',
    )
      .map((item) => ({
        ...item,
        estimatedDeliveryStart: formatDate(item.ETA_Date, 'start'),
        estimatedDeliveryEnd: formatDate(item.ETA_Date, 'end'),
      }))
      .sort((a, b) => new Date(a.ETA_Date) - new Date(b.ETA_Date));

    for (var i = 0; i < order.Pendings.length; i++) {
      let shipment = {
        isPending: true,
        status: order.Pendings[i].Status,
        Detail: [order.Pendings[i]],
        isInStock: order.Pendings[i].Status === 'In Stock',
        isBackOrder: order.Pendings[i].Status === 'On BackOrder',
        inStockItems: inStockItems,
        backOrderItems: backOrderItems,
        totalQuantity: totalPendingItems,
      };
      shipments.push(shipment);
    }

    addShipmentDetails(shipments);
  }
  if (order.Cancel.length > 0) {
    let shipments = [];
    for (var i = 0; i < order.Cancel.length; i++) {
      let shipment = {
        isCancelled: true,
        status: order.Cancel[i].Status,
        Detail: [order.Cancel[i]],
      };
      shipments.push(shipment);
    }

    addShipmentDetails(shipments);
  }

  if (order.Returns.length > 0) {
    let shipments = [];
    for (var i = 0; i < order.Returns.length; i++) {
      let shipment = {
        isReturned: true,
        status: order.Returns[i].Status,
        Detail: [order.Returns[i]],
      };
      shipments.push(shipment);
    }

    addShipmentDetails(shipments);
  }

  output += '</div></div>';
  $(`.${detailContainerClassName}`)
    .html('')
    .append(header + output);

  // Add click handler for the toggle button
  $(`.${allDetailsButton}`).on('click', toggleAllDetailsContainer);

  // set up invoice link
  $(`.${invoiceLink}`).on('click', downloadInvoice);

  window.pubsub.trigger('orderDetailRendered');
};

/**
 * switchOrderTemplate
 * Depends on existence of id, switch template
 * between list and detail
 *
 * @param {number} id - spars order id
 */
export const switchOrderTemplate = (id = null) => {
  if (id) {
    $(`.${detailClassName}`)
      .removeClass('hidden')
      .addClass(detailActiveClassName);
    displayOrderId(id, detailIdClassName);
  } else {
    $(`.${listClassName}`).removeClass('hidden').addClass(listActiveClassName);
  }
};

/**
 * renderOrderStatus
 *
 * @param {string} status - spars order status string
 */
const renderOrderStatus = (status = '') => {
  if (status === 'Delivered' || status === 'delivered') {
    status = 'Shipped';
  }
  $(`.${detailClassName}`)
    .find(`.${detailStatusClassName}`)
    .text(`: ${status}`);
};

/**
 * orderHistoryHandler
 *
 * Request orders from SPARS, cache them and render.
 */
export const orderHistoryHandler = () => {
  retrieveOrderHistory();
  $('#searchType').on('change', searchUpdated);
  $('#searchText').on('keyup', searchFieldUpdated);
  $('#searchClear').on('click', clearSearchField);
  $('#submitSearch').on('click', () => {
    searchState.currentPage = 1;
    retrieveOrderHistory();
  });
  $('.order-reset-statement--trigger').on('click', clearAllSearchFilters);
  $(`.${statusToggleClassName}`).on('click', updateStatusClass);
  $(`#${dateToggleName}`).on('change', dateUpdated);
  $('input.mobileDate-control').change(function (e) {
    $(`#${dateToggleName}`).val(this.value);
    dateUpdated(e);
  });
};

/**
 * clearAllSearchFilters
 *
 * Clear search field
 */
const clearAllSearchFilters = (e = null) => {
  $('#searchText').val('');
  $('#searchType').val('ItemName');
  searchState.currentPage = 1;
  $('.status-type--active').removeClass('status-type--active');
  $('.status-type--all').addClass('status-type--active');
  searchState.statusType = 'all';
  //Update UI to initial state
  $('#searchClear').addClass('hidden');
  $(`#${dateToggleName}`).val('6mo').prop('disabled', false);
  $(".mobileDate-control[value='6mo']").prop('checked', true);
  $(`.${statusToggleClassName}`).removeClass('status-type--disabled');
  $('.mobileDate-control').prop('disabled', false);
  $('.order-list__control-date-mobile').removeClass('opacity-50');
  clearSearchField();
  retrieveOrderHistory();
};

/**
 * clearSearchField
 *
 * Clear search field
 */
const clearSearchField = (e = null) => {
  $('#searchText').val('');
  $('#searchClear').addClass('hidden');
  //TODO: bring back auto search on search field clear when a proper search exists
  //retrieveOrderHistory();
};

/**
 * searchFieldUpdated
 *
 * Re-send the order history request with new search parameters
 */
const searchFieldUpdated = (e = null) => {
  //Submit search if enter key was pressed
  if (e && e.which == 13 && $('#searchText').val().length) {
    searchState.currentPage = 1;
    retrieveOrderHistory();
  }
  clearTimeout(searchChangedTimeout);
  searchChangedTimeout = setTimeout(function () {
    if ($('#searchText').val().length) {
      $('#searchClear').removeClass('hidden');
    } else {
      $('#searchClear').addClass('hidden');
    }
    //TODO: bring back buffered search on keyup when a proper search exists
    // retrieveOrderHistory();
  }, 1000);
};

/**
 * searchUpdated
 *
 * Re-send the order history request with new search parameters
 */
const searchUpdated = (e = null) => {
  e.preventDefault();

  const queryType = $('#searchType').val();
  searchState.currentPage = 1;
  if (queryType.indexOf('ItemName') > -1) {
    $(`#${dateToggleName}`).prop('disabled', false);
    $(`.${statusToggleClassName}`).removeClass('status-type--disabled');
    $('.mobileDate-control').prop('disabled', false);
    $('.order-list__control-date-mobile').removeClass('opacity-50');
  } else {
    $(".mobileDate-control[value='All']").prop('checked', true);
    $('.mobileDate-control').prop('disabled', 'disabled');
    $('.order-list__control-date-mobile').addClass('opacity-50');
    searchState.statusType = 'all';
    $(`#${dateToggleName}`).val('All').prop('disabled', 'disabled');
    $(`.${statusToggleClassName}`)
      .not('.status-type--all')
      .removeClass('status-type--active')
      .addClass('status-type--disabled');
    $(`.${statusToggleClassName}.status-type--all`)
      .addClass('status-type--active')
      .addClass('status-type--disabled');
  }
};

/**
 * dateUpdated
 *
 * Re-send the order history request with new date parameters
 */
const dateUpdated = (e = null) => {
  e.preventDefault();
  const newDate = $(`#${dateToggleName}`).val();
  $(".mobileDate-control[value='" + newDate + "']").prop('checked', true);
  searchState.currentPage = 1;
  retrieveOrderHistory();
};

/**
 * searchUpdated
 *
 * Re-send the order history request with new Status parameters
 */
const updateStatusClass = (e = null) => {
  e.preventDefault();
  const $target = $(e.currentTarget);
  if (
    $target.hasClass('status-type--disabled') ||
    $('#searchType').val() !== 'ItemName'
  )
    return;
  const newStatus = $target.data('status');
  $(`.${statusToggleClassName}`).removeClass('status-type--active');
  $target.addClass('status-type--active');
  const newText = $target.data('display')
    ? $target.data('display')
    : $target.data('status');
  if (searchState.statusType === newStatus) return;
  searchState.currentPage = 1;
  searchState.statusType = newStatus;
  retrieveOrderHistory();
};

/**
 * retrieveOrderHistory
 *
 * Request orders from SPARS, cache them and render.
 */
export const retrieveOrderHistory = (event) => {
  /**
   * Search fields are not wrapped in a HTML form therefore does not submit on enter
   * This handles search on Enter press
   */
  if (
    event &&
    event.target.id == 'searchText' &&
    event.type == 'keyup' &&
    event.key != 'Enter'
  )
    return false;
  // Hide or show resent messages
  const searchText = $('#searchText').val();
  const searchDate = $(`#${dateToggleName}`).val();
  if (
    searchText.length > 0 ||
    searchState.statusType !== 'all' ||
    searchDate === '3mo' ||
    searchDate === '1mo' ||
    searchDate === '1w'
  ) {
    $('.order-reset-area').removeClass('hidden');
  } else {
    $('.order-reset-area').addClass('hidden');
  }
  const request = orderRequestBuilder();
  $('#submitSearch').prop('disabled', 'disabled');

  const retrievalStartTime = Date.now();
  searchState.searchTimestamp = retrievalStartTime;
  $(`.${listContainerClassName}`)
    .addClass('loading-history')
    .removeClass('divide-y divide-grey-3')
    .append(
      `<div class="loading-message"><p class="type-serif-title-small text-grey-6 py-8">Loading order history<span class='ellipsis'><span>.</span><span>.</span><span>.</span></span></p></div>`,
    );
  $('.orders-list__container .order a')
    .attr('href', 'javascript: void(0)')
    .attr('target', '_self');
  MUAPP.orders
    .getOrders(request)
    .then((data) => {
      // Check if search request was the newest
      if (searchState.searchTimestamp === retrievalStartTime) {
        state.orders = data.orders; // Cache

        renderOrdersList(data.orders);
        $(`.${listContainerClassName}`)
          .removeClass('loading-history')
          .addClass('divide-y divide-grey-3');
        updatePagination(data);
        updateShowInventory(data);
        searchState.searchTimestamp = null;
        $('#submitSearch').prop('disabled', false);
      } else if (!searchState.searchTimestamp) {
        $(`.${listContainerClassName}`).removeClass('loading-history');
        $('#submitSearch').prop('disabled', false);
      }
    })
    .catch((err) => {
      if (searchState.searchTimestamp === retrievalStartTime) {
        const $button = $(
          '<button class="order-reset-statement--trigger underline" type="button">Retry Search</button>',
        );
        $button.on('click', clearAllSearchFilters);
        const output = `<h3 class='no-orders'>Server error encountered during search.</h3><p class="no-orders no-orders--small mt-5">Please <span id="searchErrorResetContainer"></span>.</p>`;
        $(`.${listContainerClassName}`).html('').append(output);
        $('#searchErrorResetContainer').append($button);
        $(`.${listContainerClassName}`)
          .removeClass('loading-history')
          .addClass('divide-y divide-grey-3');
        $('#submitSearch').prop('disabled', false);
      }
    });
};

/**
 * updatePagination
 *
 * Builds pagination at bottom of page based on how many pages are available.
 */
const updatePagination = (data) => {
  const total = data['total'] ? parseInt(data['total']) : data.orders.length;
  if (total > 1) {
    const perpage = data['perpage'] ? parseInt(data['perpage']) : 10;
    const currentpage = data['currentpage'] ? parseInt(data['currentpage']) : 1;
    searchState.currentPage = currentpage;
    const lastpage = data['lastpage']
      ? parseInt(data['lastpage'])
      : parseInt(total / perpage);
    const onePage = total <= perpage;
    const boundaryAmount =
      currentpage > 2 && currentpage < lastpage - 2 ? 3 : 3;
    const mobileBoundaryAmount =
      currentpage > 1 && currentpage < lastpage - 1 ? 2 : 3;
    let output = `<button class="js-pagination appearance-none js-paginate-back flex items-center gap-2 ${
      onePage || currentpage === 1 ? 'disabled' : 'available'
    }" data-page="${
      currentpage - 1
    }"><span class="caret caret-left"></span><span class="pagination-text">Previous</span></button>`;
    for (var i = 1; i <= lastpage; i++) {
      if (
        i != lastpage &&
        i != 1 &&
        (i - currentpage > boundaryAmount ||
          i - currentpage < boundaryAmount * -1)
      ) {
        continue;
      } else if (
        i != lastpage &&
        i != 1 &&
        (i - currentpage === boundaryAmount ||
          i - currentpage === boundaryAmount * -1)
      ) {
        output += `<button class="appearance-none disabled ellipsis ${
          boundaryAmount != mobileBoundaryAmount
            ? 'hidden md-up:inline-block'
            : ''
        }" data-page="${i}">&#8230;</button>`;
      } else {
        if (
          i != lastpage &&
          i != 1 &&
          (i - currentpage === mobileBoundaryAmount ||
            i - currentpage === mobileBoundaryAmount * -1)
        ) {
          output += `<button class="appearance-none disabled ellipsis md-up:hidden" data-page="${i}">&#8230;</button>`;
        }
        const mobileHide =
          i != lastpage &&
          i != 1 &&
          (i - currentpage >= mobileBoundaryAmount ||
            i - currentpage <= mobileBoundaryAmount * -1)
            ? 'hidden md-up:inline-block'
            : '';
        output += `<button class="js-pagination appearance-none js-paginate-number paginate-number ${mobileHide} ${
          onePage ? 'disabled' : 'available'
        } ${
          currentpage === i ? 'current-page' : 'normal-page'
        }" data-page="${i}">${i}</button>`;
      }
    }
    output += `<button class="js-pagination appearance-none js-paginate-next flex items-center gap-[10px]  ${
      onePage || currentpage === lastpage ? 'disabled' : 'available'
    }" data-page="${
      currentpage + 1
    }"><span class="pagination-text">Next</span><span class="caret caret-right"></span></button>`;
    $(`.${paginationContainerClassName}`).html(output);
    $(`.${paginationClassName}`).off('click', orderHistoryPaginate);
    $(`.${paginationClassName}`).on('click', orderHistoryPaginate);
  } else {
    $(`.${paginationContainerClassName}`).html('');
    $(`.${paginationClassName}`).off('click', orderHistoryPaginate);
  }
  //Update mobile query summary
  if (total > 0) {
    $('#orderResultCounter').text(data.total);
    const queryString = $('#searchText').val()
      ? ` for <strong>${$('#searchText').val()}</strong>`
      : '';
    $('#orderMobileQuery').html(queryString);
    const currentFilter = $('.status-type--active')
      .first()
      .text()
      .toLowerCase();
    $('#orderStatusType').text(`${currentFilter} orders`);
    const dateType = $(`#${dateToggleName}`).val();
    let dateString = dateText(dateType);
    if (dateString === 'All') {
      dateString = 'complete history of this account';
      $('#orderMobileDatePre').text('.');
      $('#orderMobileDate').text('');
    } else {
      $('#orderMobileDatePre').text(' within the ');
      $('#orderMobileDate').text(dateString);
    }
    $('#orderMobileQuerySummary').removeClass('hidden');
  } else {
    $('#orderMobileQuerySummary').addClass('hidden');
  }
};

const updateShowInventory = (data) => {
  const total = data['total'] ? parseInt(data['total']) : data.orders.length;
  const perpage = data['perpage'] ? parseInt(data['perpage']) : 10;
  const currentpage = data['currentpage'] ? parseInt(data['currentpage']) : 1;
  searchState.currentPage = currentpage;

  const start = Math.min((currentpage - 1) * perpage + 1, total);
  const end = Math.min(currentpage * perpage, total);

  let output = `<span class="utility-xsmall utility-xsmall--emphasis text-grey-6">${start}-${end} of ${total}</span>`;

  if (total === 0) {
    $(`.${showInventoryClassName}`).addClass('hidden');
  } else {
    $(`.${showInventoryClassName}`).removeClass('hidden');
  }

  $(`.${showInventoryClassName}`).html(output);
};

const orderHistoryPaginate = (e) => {
  const $navigation = $('.navigation__main');
  const $utility = $('.navigation__utility');

  const utilityHeight = $utility.length ? $utility.height() : 0;
  const offset = $navigation.height() + utilityHeight;

  if (
    $(e.currentTarget).hasClass('current-page') ||
    $(e.currentTarget).hasClass('disabled')
  )
    return;
  searchState.currentPage = $(e.currentTarget).data('page')
    ? $(e.currentTarget).data('page')
    : 1;
  retrieveOrderHistory();

  custom.utils.scrollTo($('#orderListing'), offset);
};

/**
 * orderRequestBuilder
 *
 * Build request to send
 */
const orderRequestBuilder = () => {
  let request = new Object();
  const query = $('#searchText').val();
  const queryType = $('#searchType').val();
  let dateType = $('#dateType').val();
  const today = new Date();
  const allDate = new Date('January 1, 2010');
  let startDate = new Date();
  if (searchState.statusType !== 'all') {
    request['Status'] = searchState.statusType;
  }
  if (query) {
    request[queryType] = query;
  }

  switch (dateType) {
    case 'All':
      startDate = allDate;
      break;
    case '1y':
      startDate.setDate(startDate.getDate() - 365);
      break;
    case '6mo':
      startDate.setDate(startDate.getDate() - 183);
      break;
    case '3mo':
      startDate.setDate(startDate.getDate() - 92);
      break;
    case '1mo':
      startDate.setDate(startDate.getDate() - 31);
      break;
    case '1w':
      startDate.setDate(startDate.getDate() - 7);
      break;
    default:
      startDate = allDate;
  }
  request['DateTo'] = today.toISOString().split('T')[0];
  request['DateFrom'] = startDate.toISOString().split('T')[0];
  request['PerPage'] = 10;
  request['Page'] = searchState.currentPage ? searchState.currentPage : 1;
  return request;
};

/**
 * orderDetailHandler
 *
 * @param {string} id - spars order id
 * Request single order and that invoice from SPARS, cache them and render.
 */
export const orderDetailHandler = (id = null) => {
  const promises = [
    MUAPP.orders.getOrderById(id),
    MUAPP.orders.getOrderInvoiceById(id),
  ];

  $.when.apply($, promises).done((order, invoice) => {
    if (order[0].Error) return permissionErrorHandler(detailContainerClassName);
    state.order = order[0].order; // Cache
    // order[0].order["Invoice"] = (invoice[0].url) ? invoice[0].url : null;

    renderOrderStatus(order[0].order.Header.OrderStatus);
    renderOrderDetail(order[0].order, id);
  });
};

/**
 * toggleAllDetailsContainer
 *
 * Toggle all details view for mobile
 */
const toggleAllDetailsContainer = () => {
  const $containerDiv = $('.order-all-details-container');
  const $page = $('.page-width');
  const $overlay = $('.page-width .bg-black');
  const $body = $('body');
  const $html = $('html');

  $containerDiv.toggleClass('disabled animate-fade');

  if ($overlay.length) {
    // Hack: disableBodyScroll and enablebodyScroll didn't work on old ios safari devices and prevented user from scrolling

    // Remove overlay
    $overlay.remove();
    // Re-enable scrolling
    $body.css({
      position: '',
      width: '',
      top: '',
      overflow: '',
    });
    $html.css('overflow', '');
    // Restore scroll position
    window.scrollTo(0, parseInt($body.data('scroll-position')));
    $body.removeData('scroll-position');
  } else {
    // Store current scroll position
    const scrollPosition = window.scrollY;
    $body.data('scroll-position', scrollPosition);

    // Add overlay
    $page
      .addClass('relative')
      .append(
        '<div class="absolute inset-0 bg-black opacity-25 transition-opacity z-2"></div>',
      );

    // Prevent scrolling on body/html while maintaining position
    $body.css({
      position: 'fixed',
      width: '100%',
      top: `-${scrollPosition}px`,
      overflow: 'hidden',
    });
    $html.css('overflow', 'hidden');

    // Allow scrolling within container
    $containerDiv.css({
      position: 'fixed',
      height: '100%',
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch',
    });
  }
};



// WEBPACK FOOTER //
// ./src/scripts/src/account/order.js